/**
 * Account theme shared module.
 *
 * Contains types, color utilities, font helpers, and pure mapping functions
 * shared between the authoring Preview (ResolvedThemeProvider) and the export
 * player (main.export.tsx). Centralising these here ensures Preview and the
 * deployed package apply identical token overrides from the same theme data.
 *
 * Intentionally excluded:
 * - DOM-specific font loading (loadAndRegisterFont) — kept app-local.
 * - Authoring UI labels (fontOptions / BASE_FONT_OPTIONS) — authoring-only.
 * - Certificate logo fields — deferred to a follow-up.
 */

import type { DeepPartial, ThemeTokens } from './themeTokens';
import { getReadableTextColor } from './colorContrast';
import type { AssistantSettings, AssistantTranslations } from './assistant';

// ─── Color utilities ─────────────────────────────────────────────────────────

/** Parse a CSS hex color string (#RGB, #RRGGBB, #RGBA, #RRGGBBAA) into r/g/b/a channels (0–255). */
function parseHex(hex: string): { r: number; g: number; b: number; a: number } | undefined {
  const cleaned = hex.trim().replace(/^#/, '');

  let r: number;
  let g: number;
  let b: number;
  let a = 255;

  if (cleaned.length === 3) {
    const [rc, gc, bc] = cleaned;
    if (!rc || !gc || !bc) return undefined;
    r = parseInt(rc + rc, 16);
    g = parseInt(gc + gc, 16);
    b = parseInt(bc + bc, 16);
  } else if (cleaned.length === 6) {
    r = parseInt(cleaned.slice(0, 2), 16);
    g = parseInt(cleaned.slice(2, 4), 16);
    b = parseInt(cleaned.slice(4, 6), 16);
  } else if (cleaned.length === 8) {
    r = parseInt(cleaned.slice(0, 2), 16);
    g = parseInt(cleaned.slice(2, 4), 16);
    b = parseInt(cleaned.slice(4, 6), 16);
    a = parseInt(cleaned.slice(6, 8), 16);
  } else {
    return undefined;
  }

  if ([r, g, b, a].some((v) => isNaN(v))) return undefined;

  return { r, g, b, a };
}

function toHex2(n: number): string {
  return Math.round(Math.max(0, Math.min(255, n)))
    .toString(16)
    .padStart(2, '0')
    .toUpperCase();
}

/**
 * Darken a hex color by `pct` percent (0–100). Returns a 7-character #RRGGBB
 * string, or `undefined` if `hex` is not a valid hex color.
 *
 * @param hex  CSS hex string, e.g. "#5B8EFF" or "#5b8eff"
 * @param pct  Darken percentage (default 10)
 */
export function deriveHover(hex: string, pct = 10): string | undefined {
  if (!hex || typeof hex !== 'string') return undefined;
  const parsed = parseHex(hex);
  if (!parsed) return undefined;

  const factor = 1 - pct / 100;
  const r = parsed.r * factor;
  const g = parsed.g * factor;
  const b = parsed.b * factor;

  return `#${toHex2(r)}${toHex2(g)}${toHex2(b)}`;
}

/**
 * Overlay a hex color with the given alpha (0–1). Returns an rgba() CSS string,
 * or `undefined` if `hex` is not a valid hex color or `alpha` is out of range.
 *
 * @param hex    CSS hex string, e.g. "#5B8EFF"
 * @param alpha  Opacity value 0–1
 */
export function withAlpha(hex: string, alpha: number): string | undefined {
  if (!hex || typeof hex !== 'string') return undefined;
  if (typeof alpha !== 'number' || isNaN(alpha) || alpha < 0 || alpha > 1) return undefined;
  const parsed = parseHex(hex);
  if (!parsed) return undefined;

  return `rgba(${parsed.r},${parsed.g},${parsed.b},${alpha})`;
}

// ─── Built-in font set + custom sentinel ─────────────────────────────────────

/** The CSS font-family stacks of every built-in option. */
export const BASE_FONT_FAMILIES: readonly string[] = [
  '"Source Sans 3", "Source Sans Pro", system-ui, sans-serif',
  'Georgia, serif',
  '"Times New Roman", Times, serif',
  '"Trebuchet MS", sans-serif',
  'Verdana, sans-serif',
  '"Courier New", Courier, monospace',
];

/** Sentinel stored in fontFamily when a custom uploaded font is active. */
export const CUSTOM_FONT_SENTINEL = '__custom__';

/** True when `family` is one of the known built-in CSS font stacks. */
export function isBuiltInFontFamily(family: string | null | undefined): family is string {
  return typeof family === 'string' && (BASE_FONT_FAMILIES as string[]).includes(family);
}

// ─── Pure font-name helpers ───────────────────────────────────────────────────

/** Stable CSS family name derived from an asset id. */
export function themeFontFamilyName(assetId: number): string {
  return `__theme_font_${assetId}__`;
}

/** Wraps a family name in the standard fallback stack. */
export function themeFontCss(familyName: string): string {
  return `"${familyName}", system-ui, sans-serif`;
}

/**
 * Weights a custom font supports, extracted server-side at upload (NA-453).
 * Static fonts have min === max and `variable: false`.
 */
export interface FontAssetMetadata {
  weight: { min: number; max: number };
  variable: boolean;
}

/**
 * FontFace descriptors for a custom font. Declaring the real weight lets the
 * browser pick the font's own bold (or its wght axis) instead of synthesizing
 * one from the 400 glyphs. Returns undefined when the metadata is missing or
 * malformed, which keeps the previous default registration.
 */
export function fontFaceDescriptors(
  metadata: FontAssetMetadata | Record<string, unknown> | null | undefined,
): FontFaceDescriptors | undefined {
  const weight = (metadata as FontAssetMetadata | null | undefined)?.weight;
  if (!weight || !Number.isFinite(weight.min) || !Number.isFinite(weight.max)) return undefined;
  if (weight.min > weight.max) return undefined;
  return { weight: weight.min === weight.max ? String(weight.min) : `${weight.min} ${weight.max}` };
}

// ─── Account theme types ──────────────────────────────────────────────────────

/**
 * Minimal DTO shape for account theme data.
 * Adapter-compatible subset (certificate excluded — deferred).
 * Used by authoring Preview (from API) and export player (from injected global).
 */
export interface AccountThemeDto {
  themeColors: {
    sidebar?: string | null;
    sidebarLogo?: string | null;
    accent?: string | null;
  } | null;
  lessonColors: {
    color1?: string | null;
    color2?: string | null;
    color3?: string | null;
    color4?: string | null;
  } | null;
  fontFamily: string | null;
  customFontAssetId?: number | null;
  primaryLogo: { url: string } | null;
  customFont: { url: string; contentType?: string; metadata?: FontAssetMetadata | null } | null;
  assistant?: AssistantSettings | null;
  assistantAvatarAssetId?: number | null;
  assistantAvatar?: { id?: number; url: string; contentType?: string; filename?: string } | null;
  assistantTranslations?: AssistantTranslations | null;
}

/** Normalised account theme values coming from the adapter or tests. */
export interface AccountThemeInput {
  /** Background color of the course navigation sidebar. */
  sidebar?: string | null;
  /** Background color of the sidebar logo band. */
  sidebarLogo?: string | null;
  /** Accent / action color (hex). */
  accent?: string | null;
  /** Resolved CSS font-family stack (built-in or already-registered custom). */
  fontFamily?: string | null;
  /** Absolute or signed URL for the primary logo. */
  primaryLogoUrl?: string | null;
  /** Per-slot lesson color palette. Null when no account theme row is saved. */
  lessonColors?: {
    color1: string | null;
    color2: string | null;
    color3: string | null;
    color4: string | null;
  } | null;
}

export interface AccountThemeAdapterResult extends AccountThemeInput {
  /**
   * True when the stored font is `__custom__` and the DTO carries a custom
   * font asset URL. The provider must register the font asynchronously and
   * then add fontFamily to the token override only after load succeeds.
   */
  requiresCustomFont: boolean;
  /** URL of the custom font asset, present only when requiresCustomFont is true. */
  customFontUrl: string | null;
  /** Asset id of the custom font, present only when requiresCustomFont is true. */
  customFontAssetId: number | null;
  /** Supported weights of the custom font; null for assets uploaded before NA-453. */
  customFontMetadata: FontAssetMetadata | null;
}

/**
 * The minimal theme payload injected into deployed packages as
 * `window.__ACCOUNT_THEME__` by the deploy controller.
 * Certificate fields are excluded (deferred).
 * Logo and font urls are rewritten to relative paths before injection.
 */
export interface ExportThemePayload {
  themeColors: {
    sidebar: string | null;
    sidebarLogo: string | null;
    accent: string | null;
  } | null;
  lessonColors: {
    color1: string | null;
    color2: string | null;
    color3: string | null;
    color4: string | null;
  } | null;
  fontFamily: string | null;
  customFontAssetId: number | null;
  primaryLogo: { url: string } | null;
  customFont: { url: string; contentType: string; metadata?: FontAssetMetadata | null } | null;
  assistant?: AssistantSettings | null;
  assistantAvatar?: { url: string } | null;
  assistantTranslations?: AssistantTranslations | null;
}

// ─── Type guard ───────────────────────────────────────────────────────────────

/**
 * Runtime type guard for `ExportThemePayload`.
 * Returns true only for structurally valid payloads (no `any` casts).
 */
export function isExportThemePayload(value: unknown): value is ExportThemePayload {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;

  // Require the discriminating fields to be present (not just valid when present).
  // An object that lacks these keys is not an ExportThemePayload.
  if (!('fontFamily' in v)) return false;
  if (!('customFontAssetId' in v)) return false;
  if (!('primaryLogo' in v)) return false;
  if (!('customFont' in v)) return false;

  // themeColors: null or object with nullable string fields
  if (v['themeColors'] !== null && v['themeColors'] !== undefined) {
    if (typeof v['themeColors'] !== 'object' || Array.isArray(v['themeColors'])) return false;
    const tc = v['themeColors'] as Record<string, unknown>;
    if (!isNullableString(tc['sidebar'])) return false;
    if (!isNullableString(tc['sidebarLogo'])) return false;
    if (!isNullableString(tc['accent'])) return false;
  }

  // lessonColors: null or object with nullable string fields
  if (v['lessonColors'] !== null && v['lessonColors'] !== undefined) {
    if (typeof v['lessonColors'] !== 'object' || Array.isArray(v['lessonColors'])) return false;
    const lc = v['lessonColors'] as Record<string, unknown>;
    if (!isNullableString(lc['color1'])) return false;
    if (!isNullableString(lc['color2'])) return false;
    if (!isNullableString(lc['color3'])) return false;
    if (!isNullableString(lc['color4'])) return false;
  }

  // fontFamily: null or string
  if (!isNullableString(v['fontFamily'])) return false;

  // customFontAssetId: null or number
  if (v['customFontAssetId'] !== null && v['customFontAssetId'] !== undefined) {
    if (typeof v['customFontAssetId'] !== 'number') return false;
  }

  // primaryLogo: null or { url: string }
  if (v['primaryLogo'] !== null && v['primaryLogo'] !== undefined) {
    if (typeof v['primaryLogo'] !== 'object' || Array.isArray(v['primaryLogo'])) return false;
    const pl = v['primaryLogo'] as Record<string, unknown>;
    if (typeof pl['url'] !== 'string') return false;
  }

  // customFont: null or { url: string, contentType: string }
  if (v['customFont'] !== null && v['customFont'] !== undefined) {
    if (typeof v['customFont'] !== 'object' || Array.isArray(v['customFont'])) return false;
    const cf = v['customFont'] as Record<string, unknown>;
    if (typeof cf['url'] !== 'string') return false;
    if (typeof cf['contentType'] !== 'string') return false;
    if (cf['metadata'] !== null && cf['metadata'] !== undefined) {
      if (typeof cf['metadata'] !== 'object' || Array.isArray(cf['metadata'])) return false;
    }
  }

  if (v['assistant'] !== null && v['assistant'] !== undefined && !isAssistantSettings(v['assistant'])) {
    return false;
  }
  if (v['assistantAvatar'] !== null && v['assistantAvatar'] !== undefined) {
    if (typeof v['assistantAvatar'] !== 'object' || Array.isArray(v['assistantAvatar'])) return false;
    if (typeof (v['assistantAvatar'] as Record<string, unknown>)['url'] !== 'string') return false;
  }
  if (v['assistantTranslations'] !== null && v['assistantTranslations'] !== undefined) {
    if (!isAssistantTranslations(v['assistantTranslations'])) return false;
  }

  return true;
}

function isAssistantSettings(value: unknown): value is AssistantSettings {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return false;
  const assistant = value as Record<string, unknown>;
  return typeof assistant['name'] === 'string'
    && typeof assistant['greeting'] === 'string'
    && (assistant['introMode'] === 'standard' || assistant['introMode'] === 'custom')
    && typeof assistant['intro'] === 'string';
}

function isAssistantTranslations(value: unknown): value is AssistantTranslations {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return false;
  return Object.values(value as Record<string, unknown>).every((translation) => {
    if (typeof translation !== 'object' || translation === null || Array.isArray(translation)) return false;
    const fields = translation as Record<string, unknown>;
    return (fields['greeting'] === undefined || typeof fields['greeting'] === 'string')
      && (fields['intro'] === undefined || typeof fields['intro'] === 'string');
  });
}

function isNullableString(value: unknown): value is string | null {
  return value === null || value === undefined || typeof value === 'string';
}

// ─── Adapter ──────────────────────────────────────────────────────────────────

/**
 * Convert an `AccountThemeDto` (from either the API or the injected export
 * payload) into an `AccountThemeAdapterResult` ready for `mapAccountThemeToTokens`.
 *
 * - themeColors fields → sidebar / sidebarLogo / accent (null when absent)
 * - primaryLogo.url → primaryLogoUrl (null when absent)
 * - built-in fontFamily → fontFamily as CSS stack string
 * - __custom__ fontFamily → requiresCustomFont = true; fontFamily left null
 *   (will be filled in by the caller after async registration)
 */
export function adaptAccountThemeDto(dto: AccountThemeDto): AccountThemeAdapterResult {
  const tc = dto.themeColors;
  const lc = dto.lessonColors;

  const sidebar = tc?.sidebar ?? null;
  const sidebarLogo = tc?.sidebarLogo ?? null;
  const accent = tc?.accent ?? null;
  const primaryLogoUrl = dto.primaryLogo?.url ?? null;

  const lessonColors = lc
    ? {
      color1: lc.color1 ?? null,
      color2: lc.color2 ?? null,
      color3: lc.color3 ?? null,
      color4: lc.color4 ?? null,
    }
    : null;

  const storedFont = dto.fontFamily;
  const isCustom = storedFont === CUSTOM_FONT_SENTINEL;

  const fontFamily = !isCustom && isBuiltInFontFamily(storedFont) ? storedFont : null;

  const customFontUrl = isCustom ? (dto.customFont?.url ?? null) : null;
  const customFontAssetId = isCustom ? (dto.customFontAssetId ?? null) : null;
  const customFontMetadata = isCustom ? (dto.customFont?.metadata ?? null) : null;

  return {
    sidebar,
    sidebarLogo,
    accent,
    fontFamily,
    primaryLogoUrl,
    lessonColors,
    requiresCustomFont: isCustom && customFontUrl !== null,
    customFontUrl,
    customFontAssetId,
    customFontMetadata,
  };
}

// ─── Token mapper ─────────────────────────────────────────────────────────────

const HEX_RE = /^#[0-9a-fA-F]{6}$/;

function isValidHex(value: string | null | undefined): value is string {
  return typeof value === 'string' && HEX_RE.test(value);
}

/**
 * Map account theme values to a `DeepPartial<ThemeTokens>` object.
 *
 * Rules:
 * - Null / undefined fields produce no token override (omitted from result).
 * - accent maps to brand.accent / brand.accentHover only.
 * - Invalid hex values for accent produce no color override for derived tokens
 *   (deriveHover / withAlpha return undefined on bad input).
 * - lessonColors slots are omitted individually if null or not a valid hex.
 */
export function mapAccountThemeToTokens(input: AccountThemeInput): DeepPartial<ThemeTokens> {
  const result: DeepPartial<ThemeTokens> = {};

  // ── Backgrounds ──────────────────────────────────────────────────────────
  if (input.sidebar != null) {
    result.backgrounds = { ...result.backgrounds, sidebar: input.sidebar };
  }
  if (input.sidebarLogo != null) {
    result.backgrounds = { ...result.backgrounds, sidebarLogo: input.sidebarLogo };
  }

  // ── Accent ────────────────────────────────────────────────────────────────
  // NA-242 scoped the account accent to a fixed set of learner surfaces that
  // read tokens.brand.accent — the exact surface list has grown since (e.g.
  // NA-271's timer welcome-back modal header) and is no longer "exactly
  // three"; see text.onAccent below and each surface's own call site for the
  // current list. Regardless of surface count, accent must NOT reach action
  // buttons (buttons.action stays on the default tokens), the learner lesson
  // sidebar (navigation.itemActiveBackground stays default), or fixed-blue UI
  // chrome and interaction states (those read ui.fixedAccent, which account
  // theming never writes).
  //
  // text.onAccent (NA-294) is derived here rather than left static: it picks
  // white or the standard dark text color, whichever contrasts better against
  // the chosen accent, so accent-colored surfaces stay legible when an
  // account picks a light accent.
  if (input.accent != null) {
    const accent = input.accent;
    const accentHover = deriveHover(accent);
    const onAccent = getReadableTextColor(accent);

    result.brand = {
      ...result.brand,
      accent,
      ...(accentHover !== undefined ? { accentHover } : {}),
    };
    result.text = { ...result.text, onAccent };
  }

  // ── Typography ────────────────────────────────────────────────────────────
  if (input.fontFamily != null) {
    result.typography = { ...result.typography, fontFamily: input.fontFamily };
  }

  // ── Assets ────────────────────────────────────────────────────────────────
  if (input.primaryLogoUrl != null) {
    result.assets = { ...result.assets, primaryLogoUrl: input.primaryLogoUrl };
  }

  // ── Lesson colors ─────────────────────────────────────────────────────────
  // Map per-slot, omitting null/undefined/invalid-hex so the default token
  // value wins for any absent or bad slot.
  if (input.lessonColors != null) {
    const lc = input.lessonColors;
    const lessonOverride: Partial<DeepPartial<ThemeTokens>['lessonColors']> = {};
    if (isValidHex(lc.color1)) lessonOverride.color1 = lc.color1;
    if (isValidHex(lc.color2)) lessonOverride.color2 = lc.color2;
    if (isValidHex(lc.color3)) lessonOverride.color3 = lc.color3;
    if (isValidHex(lc.color4)) lessonOverride.color4 = lc.color4;
    if (Object.keys(lessonOverride).length > 0) {
      result.lessonColors = lessonOverride;
    }
  }

  return result;
}
